'use client';

import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import type { User, Session } from '@supabase/supabase-js';

export type SubscriptionPlan = 'free' | 'pro' | 'pro_max';
export type SubscriptionStatus = 'active' | 'canceled' | 'past_due' | 'trialing' | 'inactive';

export interface UserProfile {
  id: string;
  email: string;
  fullName: string;
  avatarUrl: string;
  preferredLanguage: string;
  preferredCurrency: string;
  preferredTheme: string;
  subscriptionPlan: SubscriptionPlan;
  subscriptionStatus: SubscriptionStatus;
  stripeCustomerId: string;
  stripeSubscriptionId: string;
  subscriptionCurrentPeriodEnd: string | null;
  createdAt: string;
  trialStartedAt: string | null;
  trialEndsAt: string | null;
  trialPlan: SubscriptionPlan | null;
  isAdmin: boolean;
  onboardingCompleted: boolean;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: UserProfile | null;
  loading: boolean;
  signUp: (email: string, password: string, metadata?: { fullName?: string }) => Promise<unknown>;
  signIn: (email: string, password: string) => Promise<unknown>;
  signInWithGoogle: () => Promise<unknown>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
  updateProfile: (updates: Partial<UserProfile>) => Promise<void>;
  refreshProfile: () => Promise<void>;
  isEmailVerified: () => boolean;
}

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

function mapProfile(data: Record<string, unknown>): UserProfile {
  return {
    id: data.id as string,
    email: data.email as string,
    fullName: (data.full_name as string) || '',
    avatarUrl: (data.avatar_url as string) || '',
    preferredLanguage: (data.preferred_language as string) || 'en',
    preferredCurrency: (data.preferred_currency as string) || 'DZD',
    preferredTheme: (data.preferred_theme as string) || 'dark',
    subscriptionPlan: (data.subscription_plan as SubscriptionPlan) || 'free',
    subscriptionStatus: (data.subscription_status as SubscriptionStatus) || 'inactive',
    stripeCustomerId: (data.stripe_customer_id as string) || '',
    stripeSubscriptionId: (data.stripe_subscription_id as string) || '',
    subscriptionCurrentPeriodEnd: (data.subscription_current_period_end as string) || null,
    createdAt: (data.created_at as string) || '',
    trialStartedAt: (data.trial_started_at as string) || null,
    trialEndsAt: (data.trial_ends_at as string) || null,
    trialPlan: (data.trial_plan as SubscriptionPlan) || null,
    isAdmin: (data.is_admin as boolean) || false,
    onboardingCompleted: (data.onboarding_completed as boolean) ?? false,
  };
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const supabase = createClient();

  const fetchProfile = useCallback(async (userId: string) => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userId)
        .single();
      if (!error && data) {
        setProfile(mapProfile(data as Record<string, unknown>));
      }
    } catch {
      // Profile may not exist yet
    }
  }, [supabase]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id).finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        await fetchProfile(session.user.id);
      } else {
        setProfile(null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [fetchProfile, supabase.auth]);

  const signUp = async (email: string, password: string, metadata: { fullName?: string } = {}) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: metadata.fullName || '' },
        emailRedirectTo: `${process.env.NEXT_PUBLIC_SITE_URL}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signInWithGoogle = async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${process.env.NEXT_PUBLIC_SITE_URL}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setProfile(null);
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${process.env.NEXT_PUBLIC_SITE_URL}/auth/reset-password`,
    });
    if (error) throw error;
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
  };

  const updateProfile = async (updates: Partial<UserProfile>) => {
    if (!user) throw new Error('Not authenticated');
    const dbUpdates: Record<string, unknown> = {};
    if (updates.fullName !== undefined) dbUpdates.full_name = updates.fullName;
    if (updates.avatarUrl !== undefined) dbUpdates.avatar_url = updates.avatarUrl;
    if (updates.preferredLanguage !== undefined) dbUpdates.preferred_language = updates.preferredLanguage;
    if (updates.preferredCurrency !== undefined) dbUpdates.preferred_currency = updates.preferredCurrency;
    if (updates.preferredTheme !== undefined) dbUpdates.preferred_theme = updates.preferredTheme;

    const { error } = await supabase
      .from('user_profiles')
      .update(dbUpdates)
      .eq('id', user.id);
    if (error) throw error;
    await fetchProfile(user.id);
  };

  const refreshProfile = async () => {
    if (user) await fetchProfile(user.id);
  };

  const isEmailVerified = () => !!user?.email_confirmed_at;

  return (
    <AuthContext.Provider value={{
      user, session, profile, loading,
      signUp, signIn, signInWithGoogle, signOut,
      resetPassword, updatePassword, updateProfile, refreshProfile,
      isEmailVerified,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
